"use client"

import { useEffect, useState } from "react"
import { LogOut, Loader2, Plus, Trophy } from "lucide-react"
import { useWallet } from "@/lib/wallet-provider"
import { useCrystals, SATS_PER_CRYSTAL } from "@/hooks/use-crystals"
import { internalizeIncomingPayment } from "@/lib/wallet"
import { signedFetch } from "@/lib/api-auth-client"
import { IdentitySigil } from "@/components/ui/identity-sigil"

interface WalletModalProps {
  isOpen: boolean
  onClose: () => void
}

const CRYSTAL_PACKS = [100, 500, 2500]

/**
 * Wallet details modal: identity sigil + truncated key (cookie-clucker
 * style), crystal balance with buy packs, and disconnect.
 */
export const WalletModal = ({ isOpen, onClose }: WalletModalProps) => {
  const { identityKey, network, disconnect } = useWallet()
  const { balance, isPurchasing, buyCrystals } = useCrystals()
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null)

  // Ladder earnings: accrued-but-unclaimed sats, fetched each time the modal
  // opens; claiming pays out on-chain (when the server wallet is enabled).
  const [unclaimedSats, setUnclaimedSats] = useState<number | null>(null)
  const [isClaiming, setIsClaiming] = useState(false)
  const [claimMessage, setClaimMessage] = useState<string | null>(null)
  const [claimError, setClaimError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !identityKey) return
    let cancelled = false
    setClaimMessage(null)
    setClaimError(null)
    ;(async () => {
      try {
        const res = await fetch(`/api/ladder?pubkey=${encodeURIComponent(identityKey)}`)
        const data = await res.json()
        if (!cancelled && typeof data.unclaimedSats === "number") {
          setUnclaimedSats(data.unclaimedSats)
        }
      } catch {
        /* leave unclaimedSats as-is — the row just shows a dash */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, identityKey])

  if (!isOpen) return null

  const handleClaim = async () => {
    if (!identityKey || isClaiming) return
    setClaimError(null)
    setClaimMessage(null)
    setIsClaiming(true)
    try {
      // Claims are signature-bound: the wallet signs the request so only the
      // key owner can trigger their payout
      const res = await signedFetch("/api/ladder/claim", {}, identityKey)
      const data = await res.json()
      if (!res.ok) {
        setClaimError(typeof data.error === "string" ? data.error : "Claim failed")
        return
      }
      if (data.status === "paid") {
        const accepted = await internalizeIncomingPayment({
          tx: data.tx as number[],
          senderIdentityKey: data.senderIdentityKey as string,
          derivationPrefix: data.derivationPrefix as string,
          derivationSuffix: data.derivationSuffix as string,
          description: "Pelf ladder payout",
        })
        setUnclaimedSats(0)
        if (accepted) {
          setClaimMessage(`${Number(data.sats).toLocaleString()} sats claimed`)
        } else {
          setClaimError("Payout sent, but your wallet did not accept it yet.")
        }
      } else if (data.status === "accrued") {
        setClaimMessage("payouts not yet enabled")
      } else {
        setClaimMessage("Nothing to claim")
      }
    } catch {
      setClaimError("Claim failed")
    } finally {
      setIsClaiming(false)
    }
  }

  const handleBuy = async (crystals: number) => {
    setPurchaseError(null)
    setPurchaseSuccess(null)
    const result = await buyCrystals(crystals)
    if (result.ok) {
      setPurchaseSuccess(`Purchased ${crystals} crystals`)
    } else {
      setPurchaseError(result.error ?? "Purchase failed")
    }
  }

  const handleDisconnect = () => {
    disconnect()
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-game-surface-elevated border-2 border-game-border rounded-xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Identity */}
        <div className="flex items-center gap-3 mb-4">
          {identityKey && <IdentitySigil value={identityKey} size={40} className="rounded-md" />}
          <div className="min-w-0">
            <div className="text-foreground font-semibold">Connected</div>
            <div className="text-muted-foreground text-sm font-mono truncate">
              {identityKey ? `${identityKey.slice(0, 6)}…${identityKey.slice(-4)}` : "—"}
            </div>
          </div>
        </div>

        {network && network !== "mainnet" && (
          <div className="mb-3 text-xs text-amber-400 bg-amber-950/40 border border-amber-800 rounded p-2">
            Wallet is on {network} — payments require mainnet.
          </div>
        )}

        {/* Crystal balance */}
        <div className="flex items-center justify-between bg-black/30 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2">
            <img src="/images/ui/crystal.svg" alt="Crystals" width={22} height={22} style={{ imageRendering: "pixelated" }} />
            <span className="text-foreground font-semibold">{balance.toLocaleString()}</span>
            <span className="text-muted-foreground text-xs">crystals</span>
          </div>
        </div>

        {/* Buy packs */}
        <div className="mb-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Buy crystals</div>
          <div className="grid grid-cols-3 gap-2">
            {CRYSTAL_PACKS.map((pack) => (
              <button
                key={pack}
                onClick={() => handleBuy(pack)}
                disabled={isPurchasing}
                className="flex flex-col items-center gap-1 bg-black/40 hover:bg-black/60 disabled:opacity-50 border border-game-border rounded-lg py-2 transition"
              >
                {isPurchasing ? (
                  <Loader2 size={16} className="animate-spin text-foreground" />
                ) : (
                  <span className="flex items-center gap-1 text-foreground font-semibold text-sm">
                    <Plus size={12} />
                    {pack}
                  </span>
                )}
                <span className="text-muted-foreground text-[10px]">
                  {(pack * SATS_PER_CRYSTAL).toLocaleString()} sats
                </span>
              </button>
            ))}
          </div>
          {purchaseError && <div className="mt-2 text-xs text-red-400">{purchaseError}</div>}
          {purchaseSuccess && <div className="mt-2 text-xs text-green-400">{purchaseSuccess}</div>}
        </div>

        {/* Ladder earnings */}
        <div className="mb-4">
          <div className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Ladder earnings</div>
          <div className="flex items-center justify-between bg-black/30 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Trophy size={18} className="text-amber-400" />
              <span className="text-foreground font-semibold">
                {unclaimedSats == null ? "—" : unclaimedSats.toLocaleString()}
              </span>
              <span className="text-muted-foreground text-xs">sats</span>
            </div>
            <button
              onClick={handleClaim}
              disabled={isClaiming || !unclaimedSats}
              className="flex items-center gap-1 bg-black/40 hover:bg-black/60 disabled:opacity-50 border border-game-border rounded-lg px-3 py-1.5 text-sm font-semibold text-foreground transition"
            >
              {isClaiming ? <Loader2 size={14} className="animate-spin" /> : "Claim"}
            </button>
          </div>
          {claimError && <div className="mt-2 text-xs text-red-400">{claimError}</div>}
          {claimMessage && <div className="mt-2 text-xs text-green-400">{claimMessage}</div>}
        </div>

        {/* Disconnect */}
        <button
          onClick={handleDisconnect}
          className="w-full flex items-center justify-center gap-2 bg-game-danger hover:bg-game-danger-hover text-game-danger-foreground rounded-lg py-2 transition"
        >
          <LogOut size={16} />
          Disconnect
        </button>
      </div>
    </div>
  )
}
